import React, { useMemo } from "react";
import { defineMessages, useIntl } from "react-intl";

import Account from "@/components/account";
import DropdownMenu from "@/components/dropdown-menu";
import { useAuth } from "@/contexts/auth/useAuth";
import { useModalContext } from "@/contexts/modal-context";
import { useFeatures } from "@/hooks";

import ThemeToggle from "./theme-toggle";

import type { Account as AccountSchema } from "@/schemas";

const messages = defineMessages({
  add: {
    id: "profile_dropdown.add_account",
    defaultMessage: "Add an existing account",
  },
  addAccount: {
    id: "profile_dropdown.create_account",
    defaultMessage: "Create new account",
  },
  theme: { id: "profile_dropdown.theme", defaultMessage: "Theme" },
  logout: { id: "profile_dropdown.logout", defaultMessage: "Log out @{acct}" },
});

interface ProfileDropdownProps {
  account: AccountSchema;
  children: React.ReactNode;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ account, children }) => {
  const { openModal } = useModalContext();
  const features = useFeatures();
  const intl = useIntl();

  const { otherAccounts, logOut, switchAccount } = useAuth();

  const handleLogOut = () => logOut();

  const handleSwitchAccount = (account: AccountSchema) => {
    switchAccount(account);
  };

  const renderAccount = (account: AccountSchema) => {
    return (
      <Account
        account={account}
        showProfileHoverCard={false}
        withLinkToProfile={false}
        hideActions
      />
    );
  };

  const menu = useMemo(() => {
    const menu = [];

    if (otherAccounts.length > 0) {
      menu.push({ text: renderAccount(account), to: `/@${account.acct}` });
    }

    otherAccounts.forEach((otherAccount: AccountSchema) => {
      menu.push({
        text: renderAccount(otherAccount),
        action: () => handleSwitchAccount(otherAccount),
      });
    });

    if (otherAccounts.length > 1) {
      menu.push({ text: null });
    }

    menu.push({
      text: intl.formatMessage(messages.theme),
      toggle: <ThemeToggle />,
    });
    menu.push(null);

    menu.push({
      text: intl.formatMessage(messages.add),
      action: () => {
        openModal("SIGNIN");
      },
    });

    menu.push({
      text: intl.formatMessage(messages.addAccount),
      action: () => {
        openModal("REGISTRATION");
      },
    });

    menu.push(null);

    menu.push({
      text: intl.formatMessage(messages.logout, { acct: account.acct }),
      action: handleLogOut,
    });

    return menu;
  }, [account, otherAccounts, features]);

  return (
    <DropdownMenu
      items={menu}
      width="content"
      placement="top-end"
      position="fixed"
    >
      <button type="button">{children}</button>
    </DropdownMenu>
  );
};

export default ProfileDropdown;
